import React, { useRef, useState } from 'react';
import { formatPrice } from '../../../services/formatPrice';
import { TextInput } from '../../Form';
import useCheckoutFormHelpers from '../../../hooks/useCheckoutFormHelpers';
import useCheckoutState from '../../../hooks/useCheckoutState';
import ErrorMessage from './ErrorMessage';
import { FormFieldItem } from './FormFieldItem';
import Label from './Label';
import Spinner from '../../Spinner';
import useCartCost from '../../../hooks/useCartCost';
import useSelectedPrice from '../../../hooks/useSelectedPrice';
import { isRecurringPurchase } from '../../../lib/multiPrice';
import useCheckoutCoupon from '../../../hooks/useCheckoutCoupon';
import getThemeLabels from '../../../lib/getThemeLabels';
import Button from '../../Button';
import StyledElement from '../../StyledElement';
import FocusableInput from '../../CheckoutForm/components/FormField/FocusableInput';
import { FocusProvider } from '../../../contexts/FocusContext';
import clsx from 'clsx';

function CouponField() {
  const couponCodeField = useRef();
  const { form, coupon, removeCoupon, product, theme, currencyLocale } = useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();
  const { validateCoupon } = useCheckoutCoupon();
  const { planIterations, currency, couponReachesCart } = useCartCost();
  const selectedPrice = useSelectedPrice();

  const [isValidatingCoupon, setIsValidatingCoupon] = useState('');
  const [couponError, setCouponError] = useState('');

  const labels = getThemeLabels(theme);

  async function handleValidateCoupon() {
    try {
      setIsValidatingCoupon(true);
      setCouponError('');

      await validateCoupon(form.discountCode);

      setFormStateByKey('discountCode', '');
      setIsValidatingCoupon(false);
    } catch (error) {
      setIsValidatingCoupon(false);
      couponCodeField.current?.focus();
      setCouponError(error.message);
    }
  }

  return (
    <>
      <FocusProvider groupId="discountCode">
        <FormFieldItem className="cp_form-item">
          <StyledElement
            elementPath="checkout.form.fields.label"
            as={Label}
            htmlFor="discountCode"
            className={clsx('cp_form-label')}
          >
            {labels.discountCodeText}
          </StyledElement>

          {!coupon && (
            <>
              <div className="cp_coupon-form relative">
                <FocusableInput elementId="discountCode">
                  <StyledElement
                    elementPath="checkout.form.fields.discountCode"
                    as={TextInput}
                    innerRef={couponCodeField}
                    className={clsx('cp_form-textinput')}
                    id="discountCode"
                    name="discountCode"
                    type="text"
                    placeholder={labels.discountCodePlaceholderText}
                    value={form.discountCode}
                    onChange={e => setFormStateByKey('discountCode', e.target.value)}
                    onKeyPress={e => {
                      if (e.which === 13 && form.discountCode) {
                        e.preventDefault();
                        handleValidateCoupon();
                      }
                    }}
                  />
                </FocusableInput>

                <Button
                  appearance="bare"
                  type="button"
                  disabled={!form.discountCode || isValidatingCoupon}
                  onClick={handleValidateCoupon}
                  className="cp_button cp_coupon-apply-button absolute right-1 inset-y-0 my-auto h-10 w-16 justify-center !text-[var(--cp_checkout-form-fields-discountCode-applyButton-color)]"
                >
                  {isValidatingCoupon && <Spinner width="32px" height="32px" />}
                  {!isValidatingCoupon && labels.discountCodeApplyText}
                </Button>
              </div>
            </>
          )}

          {couponError && couponError.length > 0 && (
            <ErrorMessage variant="error" className="cp_error cp_coupon-error">
              {couponError}
            </ErrorMessage>
          )}

          {coupon && (
            <div className="cp_coupon flex flex-wrap gap-x-2 items-baseline mt-2 min-w-0">
              <div className="cp_coupon-code min-w-0 max-w-full">
                <div className="cp_coupon-code-text-wrapper bg-[var(--cp_checkout-form-fields-discountCode-code-background-color)] rounded flex items-center justify-start py-1 gap-x-1">
                  <span className="cp_coupon-code-text text-[var(--cp_checkout-form-fields-discountCode-code-color)] [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)] [font-weight:var(--cp_font-body-weight)] uppercase pl-2 min-w-0 break-all">
                    {coupon.code}
                  </span>

                  <button
                    onClick={() => removeCoupon()}
                    type="button"
                    aria-label="Remove discount code"
                    className="cp_coupon-remove fill-[var(--cp_checkout-form-fields-discountCode-code-color)] opacity-75 hover:opacity-100 transition-all mt-px px-1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24px"
                      height="24px"
                    >
                      <path d="M16.24 14.83a1 1 0 0 1-1.41 1.41L12 13.41l-2.83 2.83a1 1 0 0 1-1.41-1.41L10.59 12 7.76 9.17a1 1 0 0 1 1.41-1.41L12 10.59l2.83-2.83a1 1 0 0 1 1.41 1.41L13.41 12l2.83 2.83z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="cp_coupon-discount text-[var(--cp_checkout-form-fields-discountCode-discount-color)] [font-weight:var(--cp_font-body-weight)] [font-size:var(--cp_font-body-size-sm)] [line-height:var(--cp_font-body-line-height-sm)]">
                {/* A coupon that picks items this cart lacks is still applied, and takes nothing. */}
                {!couponReachesCart || coupon.amountOff ? (
                  <>
                    -
                    {formatPrice({
                      amount: couponReachesCart ? coupon.amountOff : 0,
                      currency: currency,
                      locale: currencyLocale,
                      style: 'currency',
                    })}
                  </>
                ) : coupon.percentOff ? (
                  <>{coupon.percentOff}%</>
                ) : null}

                {/* TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type via isRecurringPurchase) */}
                {isRecurringPurchase(selectedPrice, product) && !planIterations && (
                  <>
                    {coupon.duration === 'once' && ' (once)'}
                    {coupon.duration === 'forever' && ' (forever)'}
                    {coupon.duration === 'repeating' && ` (${coupon.durationInMonths} months)`}
                  </>
                )}
              </div>
            </div>
          )}
        </FormFieldItem>
      </FocusProvider>
    </>
  );
}

export default CouponField;
